import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth, db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

interface UserStats {
  tokens: number;
  gamesPlayed: number;
  highScores: {
    [key: string]: number;
  };
  displayName?: string;
  isAdmin?: boolean;
}

interface UserContextType {
  userName: string;
  stats: UserStats | null;
  loading: boolean;
  refreshUserData: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({
  userName: '',
  stats: null,
  loading: true,
  refreshUserData: async () => {}
});

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [userName, setUserName] = useState('');
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user?.email) {
        setUserName(user.email.split('@')[0]);
        
        const userRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(userRef);
        
        if (docSnap.exists()) {
          setStats(docSnap.data() as UserStats);
        }
      } else {
        setUserName('');
        setStats(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const refreshUserData = async () => {
    const user = auth.currentUser;
    if (user?.email) {
      const userRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(userRef);
      
      if (docSnap.exists()) {
        setStats(docSnap.data() as UserStats);
      }
    }
  };

  // Add loading screen
  if (loading) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-white text-xl">Loading...</div>
    </div>;
  }

  return (
    <UserContext.Provider value={{ userName, stats, loading, refreshUserData }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext); 